import _ from 'lodash';

import dbDefinitionMapperUtilities, {
    FieldsDefinition,
    FieldTypesDefinition,
    TableDefinition
} from './DbDefinitionMapperUtilities';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';

export type Views = {
    [ viewName : string ] : DbView;
}

class DbView {

    /**
     * The database schema this view(s) belongs to
     */
    protected schema : string;

    /**
     * The database table this view(s) arte for
     */
    protected table : string;

    /**
     * The mapping between attribute name and database field name for the 'active' table
     */
    protected fieldMap : FieldsDefinition;

    /**
     * The mapping between database field name and attribute name for the 'active' table
     */
    protected dbFieldMap : FieldsDefinition;

    /**
     * The mapping between attribute names and database field types of both 'active' and 'history' table
     */
    protected fieldTypesMap : FieldTypesDefinition;

    /**
     * The mapping between attribute name and database field name for the 'history' table
     */
    protected historyFieldMap : FieldsDefinition;

    /**
     * The mapping between database field name and attribute name for the 'history' table
     */
    protected historyDbFieldMap : FieldsDefinition;

    /**
     * Exists a generic view for the table
     */
    protected hasView : boolean = true;
    /**
     * The prefix for the table to resolve the generic view with
     */
    protected viewPrefix : string = 'V_';

    /**
     * Exists a view for the status 'active' of the table
     */
    protected hasActiveView : boolean = true;
    /**
     * The prefix for the table to resolve the 'active' view with
     */
    protected activeViewPrefix : string = 'V_A';

    /**
     * Exists a view for the status 'active' and 'planned' of the table
     */
    protected hasActivePlannedView : boolean = false;
    /**
     * The prefix for the table to resolve the 'active' and 'planned' view with
     */
    protected activePlannedViewPrefix : string = 'V_AP';

    /**
     * Exists a view for the status 'active', 'planned' and 'template' of the table
     */
    protected hasActivePlannedTemplateView : boolean = false;
    /**
     * The prefix for the table to resolve the 'active', 'planned' and 'template' view with
     */
    protected activePlannedTemplateViewPrefix : string = 'V_APT';

    /**
     * Exists a view for the status 'planned' of the table
     */
    protected hasPlannedView : boolean = false;
    /**
     * The prefix for the table to resolve the 'planned' view with
     */
    protected plannedViewPrefix : string = 'V_P';

    /**
     * Exists a view for the status 'template' of the table
     */
    protected hasTemplateView : boolean = false;
    /**
     * The prefix for the table to resolve the 'template' view with
     */
    protected templateViewPrefix : string = 'V_T';

    /**
     * Exists a view for the import table of the table
     */
    protected hasImportedView : boolean = false;
    /**
     * The prefix for the table to resolve the 'imported' view with
     */
    protected importedViewPrefix : string = 'V_S';

    /**
     *  Exists a history table for the table
     */
    protected hasHistoryTable : boolean = true;
    /**
     * The prefix for the table to resolve the history table with
     */
    protected historyTablePrefix : string = 'H';
    /**
     * Exists a history view for the table
     */
    protected hasHistoryView : boolean = true;
    /**
     * The prefix for the table to resolve the history view with
     */
    protected historyViewPrefix : string = 'V_H';
    /**
     * Exists a history snapshot view for the table
     */
    protected hasHistorySnapshot : boolean = false;
    /**
     * The prefix for the table to resolve the history snapshot view with
     */
    protected historySnapshotPrefix : string = 'V_HSNAP_';

    /**
     * The name of this view
     */
    protected _name : string;

    constructor( name : string, schema : string, definition : TableDefinition ) {
        this._name = name.toUpperCase();
        this.schema = schema.toUpperCase();

        this.table = definition.table;

        this.fieldMap = definition.fieldMap;
        // flip keys and values for dbFieldMap
        this.dbFieldMap = {};
        Object.keys( this.fieldMap ).forEach( key => {
            key = key as string;
            this.dbFieldMap[ this.fieldMap[ key ].toUpperCase() ] = key;
        } );

        this.historyFieldMap = definition.historyFieldMap ? definition.historyFieldMap : {};
        // flip keys and values for historyDbFieldMap
        this.historyDbFieldMap = {};
        Object.keys( this.historyFieldMap ).forEach( key => {
            key = key as string;
            this.historyDbFieldMap[ this.historyFieldMap[ key ].toUpperCase() ] = key;
        } );

        this.fieldTypesMap = definition.fieldTypesMap ? definition.fieldTypesMap : {};

        if ( definition.hasView !== undefined ) {
            this.hasView = definition.hasView;
        }
        if ( definition.viewPrefix ) {
            this.viewPrefix = definition.viewPrefix;
        }

        if ( definition.hasActiveView !== undefined ) {
            this.hasActiveView = definition.hasActiveView;
        }
        if ( definition.activeViewPrefix ) {
            this.activeViewPrefix = definition.activeViewPrefix;
        }

        if ( definition.hasActivePlannedView !== undefined ) {
            this.hasActivePlannedView = definition.hasActivePlannedView;
        }
        if ( definition.activePlannedViewPrefix ) {
            this.activePlannedViewPrefix = definition.activePlannedViewPrefix;
        }

        if ( definition.hasActivePlannedTemplateView !== undefined ) {
            this.hasActivePlannedTemplateView = definition.hasActivePlannedTemplateView;
        }
        if ( definition.activePlannedTemplateViewPrefix ) {
            this.activePlannedTemplateViewPrefix = definition.activePlannedTemplateViewPrefix;
        }

        if ( definition.hasPlannedView !== undefined ) {
            this.hasPlannedView = definition.hasPlannedView;
        }
        if ( definition.plannedViewPrefix ) {
            this.plannedViewPrefix = definition.plannedViewPrefix;
        }

        if ( definition.hasTemplateView !== undefined ) {
            this.hasTemplateView = definition.hasTemplateView;
        }
        if ( definition.templateViewPrefix ) {
            this.templateViewPrefix = definition.templateViewPrefix;
        }

        if ( definition.hasImportedView !== undefined ) {
            this.hasImportedView = definition.hasImportedView;
        }
        if ( definition.importedViewPrefix ) {
            this.importedViewPrefix = definition.importedViewPrefix;
        }

        if ( definition.hasHistoryTable !== undefined ) {
            this.hasHistoryTable = definition.hasHistoryTable;
        }
        if ( definition.historyTablePrefix ) {
            this.historyTablePrefix = definition.historyTablePrefix;
        }
        if ( definition.hasHistoryView !== undefined ) {
            this.hasHistoryView = definition.hasHistoryView;
        }
        if ( definition.historyViewPrefix ) {
            this.historyViewPrefix = definition.historyViewPrefix;
        }
        if ( definition.hasHistorySnapshot !== undefined ) {
            this.hasHistorySnapshot = definition.hasHistorySnapshot;
        }
        if ( definition.historySnapshotPrefix ) {
            this.historySnapshotPrefix = definition.historySnapshotPrefix;
        }
    }

    /**
     * Getter for the appropriate view for current context
     */
    get name() : string {
        return `${this.getTablePrefix()}${this.table.toUpperCase()}`;
    }

    /**
     * Getter for the appropriate view for current context prefixed with the schema
     */
    get fullName() : string {
        return `${this.schema.toUpperCase()}.${this.getTablePrefix()}${this.table.toUpperCase()}`;
    }

    /**
     * Getter for the table this view(s) are for
     */
    get tableName() : string {
        return `${this.table.toUpperCase()}`;
    }

    /**
     * Getter for the table this view(s) are for prefixed with the schema
     */
    get fullTableName() : string {
        return `${this.schema.toUpperCase()}.${this.table.toUpperCase()}`;
    }

    /**
     * Getter for the attribute name to database field name mapping of the appropriate view for current context
     */
    get fields() : FieldsDefinition {
        const isHistoryRequest = dbDefinitionMapperUtilities.isHistoryRequest ?? global.isHistoryRequest;

        let retVal = this.fieldMap;

        if ( isHistoryRequest && Object.keys( this.historyFieldMap ).length ) {
            retVal = this.historyFieldMap;
        }

        return retVal;
    }

    /**
     * Getter for the database field name to attribute name mapping of the appropriate view for current context
     */
    get dbFields() : FieldsDefinition {
        const isHistoryRequest = dbDefinitionMapperUtilities.isHistoryRequest ?? global.isHistoryRequest;

        let retVal = this.dbFieldMap;

        if ( isHistoryRequest && Object.keys( this.historyDbFieldMap ).length ) {
            retVal = this.historyDbFieldMap;
        }

        return retVal;
    }

    /**
     * Getter for attribute name to database field type mapping of both 'active' and 'history' view(s)
     */
    get fieldTypes() : FieldTypesDefinition {
        return this.fieldTypesMap;
    }

    /**
     * Computes the view prefix for given tableDef, dbDefinitionMapperUtilities.status and global.timestamp request attributes
     *
     * status mapping for CFG, DATA and current time (timestamp === 'now')
     *
     *     active
     *      - description:         active status ( 7 )
     *      - data mapped to:      V_ACI_...
     *      - metamodel mapped to: V_ACI_...
     *
     *     history
     *      - description:         history status ( 21 )
     *      - data mapped to:      V_CI_... ( you have to set the status ( history ) filter by yourself )
     *      - metamodel mapped to: V_ACI_...
     *
     *     deleted
     *      - description:         deleted status ( -1 )
     *      - data mapped to:      V_HSNAP_CI... ( you have to set the status ( deleted ) filter by yourself )
     *      - metamodel mapped to: V_HSNAP_CI... ( you have to set the status ( active ) filter by yourself )
     *
     *     all
     *      - description:         all status, therefor mapped into history tables
     *      - data mapped to:      V_HSNAP_CI...
     *      - metamodel mapped to: V_HSNAP_CI... ( you have to set the status ( active ) filter by yourself )
     *
     * status mapping for DATA only and current time (timestamp === 'now')
     *
     *     all-non-history
     *      - description:         all status without history status ( 21 ) and deleted status ( -1 )
     *      - data mapped to:      V_APTCI_...
     *      - metamodel mapped to: V_ACI...
     *
     *     active-planned ( ap, apci )
     *      - description:         active and planned status ( 7, 193 )
     *      - data mapped to:      V_APCI_...
     *      - metamodel mapped to: V_ACI_...
     *
     *     active-planned-template ( apt, aptci )
     *      - description:         active, planned and template status ( 7, 193, 201 )
     *      - data mapped to:      V_APTCI_...
     *      - metamodel mapped to: V_ACI_...
     *
     *     planned
     *      - description:         planned status ( 193 )
     *      - data mapped to:      V_PCI_...
     *      - metamodel mapped to: V_ACI_...
     *
     *     template
     *      - description:         template status ( 201 )
     *      - data mapped to:      V_TCI_...
     *      - metamodel mapped to: V_ACI_...
     *
     *     imported ( scan, sci )
     *      - description:         import tables
     *      - data mapped to:      V_SCI_...
     *      - metamodel mapped to: V_ACI_...
     *
     * mapping for CFG, DATA and history time (timestamp !== 'now')
     *
     *      - data mapped to:      V_HSNAP_CI... ( you have to set the status filter by yourself )
     *      - metamodel mapped to: V_HSNAP_CI... ( you have to set the status filter by yourself )
     *
     * @return {string}
     */
    getTablePrefix() : string {
        // FIXME: hier nicht die Klassenvariable nehmen, sondern die Objektvariable this.status. So kann man diese Klasse nicht instanziieren
        const status            = dbDefinitionMapperUtilities.status ?? global.status,
              isHistoryRequest  = dbDefinitionMapperUtilities.isHistoryRequest ?? global.isHistoryRequest ?? false,
              noHistorySnapshot = dbDefinitionMapperUtilities.noHistorySnapshot ?? global.noHistorySnapshot ?? false;

        let retVal = '';

        // history
        if ( isHistoryRequest && ( this.hasHistorySnapshot || this.hasHistoryView || this.hasHistoryTable ) ) {
            if ( !noHistorySnapshot && this.hasHistorySnapshot ) {
                retVal = this.historySnapshotPrefix;
            }
            else if ( this.hasHistoryView ) {
                retVal = this.historyViewPrefix;
            }
            else if ( this.hasHistoryTable ) {
                retVal = this.historyTablePrefix;
            }
        }

        else {
            let viewIdentifier : 'active' | 'planned' | 'template' | 'history' | 'activePlanned' | 'activePlannedTemplate' | 'imported' | 'allNonHistory';

            // TODO: remove, when vset in DATA finder is adapted to new syntax
            switch ( status ) {
                case 'all':
                    viewIdentifier = 'history';
                    break;
                case 'all-non-history':
                    viewIdentifier = 'allNonHistory';
                    break;
                case 'active-planned':
                    viewIdentifier = 'activePlanned';
                    break;
                case 'active-planned-template':
                    viewIdentifier = 'activePlannedTemplate';
                    break;
                case 'deleted':
                    viewIdentifier = 'history';
                    break;
                default:
                    viewIdentifier = status as 'active' | 'planned' | 'template' | 'history' | 'activePlanned' | 'activePlannedTemplate' | 'imported' | 'allNonHistory';
            }

            const viewPrefixAccessor        = `${viewIdentifier}ViewPrefix`,
                  hasViewIdentifierAccessor = `has${_.upperFirst( viewIdentifier )}View`;

            // has view for status
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if ( this[ hasViewIdentifierAccessor ] ) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retVal = this[ viewPrefixAccessor ];
            }

            // has active view
            else if ( viewIdentifier !== 'allNonHistory' && this.hasActiveView ) {
                retVal = this.activeViewPrefix;
            }

            // has generic view
            else if ( this.hasView ) {
                retVal = this.viewPrefix;
            }

            // has active view
            else if ( this.hasActiveView ) {
                retVal = this.activeViewPrefix;
            }

        }

        retVal = retVal.toUpperCase();

        return retVal;
    }

    /**
     * Returns the database fields type for given field.
     */
    getFieldType( field : string ) : PropertyValueType | undefined {
        return this.fieldTypesMap?.[ field ];
    }

    toString() : string {
        return this.tableName;
    }

}

export default DbView;
